import type { DirectoryOffer, DirectoryProduct } from './aggregate-releases'
import type { ProductSpecs } from './release-specs'

// Named tuple fields keep the wire format explicit without repeating nineteen
// property names for every model/level in the directory.
export type PackedDirectoryProduct = [
  slug: string,
  modelId: number | null,
  htScore: number | null,
  soundScore: number | null,
  image: number,
  fromPrice: number | null,
  offer: number,
  quietAudioUrl: number,
  level: number | null,
  primary: boolean,
  classification: number,
  tags: number,
  channels: number,
  hardware: number,
  software: number,
  loss: number,
  style: number,
  accessories: number,
  specs: number,
]

export interface ProductPacking {
  word: (value: string) => number
  list: (value: string[]) => number
  offer: (value: DirectoryOffer | null) => number
  specs: (value: ProductSpecs) => number
  image: (value: string | null) => number
  audio: (value: string | null) => number
}

export const packProduct = (p: DirectoryProduct, pack: ProductPacking): PackedDirectoryProduct => [
  p.slug,
  p.modelId,
  p.htScore,
  p.soundScore,
  pack.image(p.image),
  p.fromPrice,
  pack.offer(p.offer),
  pack.audio(p.quietAudioUrl),
  p.level,
  p.primary,
  pack.word(p.classification),
  pack.list(p.tags),
  pack.list(p.channels),
  pack.list(p.hardware),
  pack.list(p.software),
  pack.list(p.loss),
  pack.word(p.style),
  pack.list(p.accessories),
  pack.specs(p.specs),
]

export interface ProductUnpacking {
  word: (value: number) => string
  list: (value: number) => string[]
  offer: (value: number) => DirectoryOffer | null
  specs: (value: number) => ProductSpecs
  image: (value: number) => string | null
  audio: (value: number) => string | null
}

const unpackProductIdentity = (packed: PackedDirectoryProduct, unpack: ProductUnpacking) => {
  const [slug, modelId, htScore, soundScore, image, fromPrice, offer, quietAudioUrl, level, primary] = packed
  return {
    slug,
    modelId,
    htScore,
    soundScore,
    image: unpack.image(image),
    fromPrice,
    offer: unpack.offer(offer),
    quietAudioUrl: unpack.audio(quietAudioUrl),
    level,
    primary,
  }
}

export const unpackProduct = (packed: PackedDirectoryProduct, unpack: ProductUnpacking): DirectoryProduct => ({
  ...unpackProductIdentity(packed, unpack),
  classification: unpack.word(packed[10]),
  tags: unpack.list(packed[11]),
  channels: unpack.list(packed[12]),
  hardware: unpack.list(packed[13]),
  software: unpack.list(packed[14]),
  loss: unpack.list(packed[15]),
  style: unpack.word(packed[16]),
  accessories: unpack.list(packed[17]),
  specs: unpack.specs(packed[18]),
})
